import { useMemo } from 'react';
import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

export interface ServiceHealthResponse {
  status: 'OK' | 'DEGRADED' | 'healthy' | 'unhealthy';
  timestamp: string;
  services?: Record<string, string>;
  features?: Record<string, unknown>;
  redis?: string;
  uptime?: number;
  error?: string;
}

export type ServiceId = 'profiles' | 'chat' | 'config';

export interface ServiceDefinition {
  id: ServiceId;
  label: string;
  endpoint: string;
}

const serviceDefinitions: ServiceDefinition[] = [
  { id: 'profiles', label: 'Profiles', endpoint: '/profiles/health' },
  { id: 'chat', label: 'Chat', endpoint: '/chat/health' },
  { id: 'config', label: 'Config', endpoint: '/config/health' },
];

export const useServiceHealth = () => {
  const client = useApiClient();

  const queries = useQueries({
    queries: serviceDefinitions.map((service) => ({
      queryKey: ['service-health', service.id],
      queryFn: () => client.get<ServiceHealthResponse>(service.endpoint),
      staleTime: 1000 * 30,
      refetchInterval: 1000 * 60,
    })),
  });

  return useMemo(
    () =>
      serviceDefinitions.map((service, index) => ({
        definition: service,
        query: queries[index] as UseQueryResult<ServiceHealthResponse>,
      })),
    [queries],
  );
};
